import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

const ITEMS = [
  { k: "services", l: "Services", h: "View what we build" },
  { k: "work", l: "Work", h: "Browse case studies" },
  { k: "process", l: "Process", h: "How we work" },
  { k: "about", l: "About", h: "Meet the team" },
  { k: "contact", l: "Contact", h: "Start a project" },
  { k: "components", l: "Components", h: "Open showcase" },
];

export function CommandPalette({ testId = "cmdk" }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
    else setQ("");
  }, [open]);

  const filtered = ITEMS.filter(
    (i) =>
      i.l.toLowerCase().includes(q.toLowerCase()) ||
      i.h.toLowerCase().includes(q.toLowerCase()),
  );

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={`${testId}-trigger`}
        className="inline-flex items-center gap-2 rounded-full border border-hairline-strong bg-glass px-4 py-2 text-sm text-secondary-x hover-glass transition-colors"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="11" cy="11" r="7" />
          <path d="M21 21l-4.3-4.3" />
        </svg>
        Search…
        <kbd className="ml-3 inline-flex h-5 items-center rounded border border-hairline-strong bg-canvas px-1.5 text-[10px] font-mono-x text-muted-x">
          ⌘K
        </kbd>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid={`${testId}-overlay`}
            className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-md flex items-start justify-center pt-[12vh] px-4"
            onClick={() => setOpen(false)}
          >
            <motion.div
              initial={{ y: -20, opacity: 0, scale: 0.98 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: -10, opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              data-testid={`${testId}-panel`}
              className="w-full max-w-xl rounded-2xl border border-hairline-strong bg-surface-2 shadow-[0_30px_120px_-30px_rgba(0,0,0,0.6)] overflow-hidden"
            >
              <div className="flex items-center gap-3 px-5 py-4 border-b border-hairline">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-muted-x">
                  <circle cx="11" cy="11" r="7" />
                  <path d="M21 21l-4.3-4.3" />
                </svg>
                <input
                  ref={inputRef}
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  data-testid={`${testId}-input`}
                  placeholder="Type to search the studio…"
                  className="flex-1 bg-transparent outline-none text-primary-x placeholder:text-muted-x text-sm"
                />
                <kbd className="text-[10px] font-mono-x text-muted-x">ESC</kbd>
              </div>
              <ul className="max-h-80 overflow-auto py-2">
                {filtered.length === 0 && (
                  <li className="px-5 py-6 text-sm text-muted-x">No matches.</li>
                )}
                {filtered.map((i) => (
                  <li key={i.k}>
                    <button
                      type="button"
                      onClick={() => setOpen(false)}
                      className="w-full text-left px-5 py-3 flex items-center justify-between hover:bg-glass-hover transition-colors"
                    >
                      <div>
                        <div className="text-primary-x text-sm">{i.l}</div>
                        <div className="text-muted-x text-xs">{i.h}</div>
                      </div>
                      <span className="text-muted-x text-xs">↵</span>
                    </button>
                  </li>
                ))}
              </ul>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

/** macOS-style dock with magnification on hover. */
export function Dock({ items, testId = "dock" }) {
  const [mx, setMx] = useState(null);
  return (
    <div
      data-testid={testId}
      onMouseLeave={() => setMx(null)}
      onMouseMove={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        setMx(e.clientX - r.left);
      }}
      className="inline-flex items-end gap-2 rounded-2xl border border-hairline-strong bg-glass backdrop-blur-2xl px-4 py-3"
    >
      {items.map((it, i) => {
        const center = 32 + i * 56;
        const dist = mx == null ? 999 : Math.abs(mx - center);
        const scale = mx == null ? 1 : Math.max(1, 1.6 - dist / 120);
        return (
          <motion.button
            key={it.k}
            animate={{ scale }}
            transition={{ type: "spring", stiffness: 300, damping: 24 }}
            className="h-12 w-12 rounded-xl bg-surface-2 border border-hairline-strong flex items-center justify-center text-primary-x text-lg"
            title={it.l}
            data-testid={`${testId}-${it.k}`}
          >
            {it.icon}
          </motion.button>
        );
      })}
    </div>
  );
}
